/**
 * 承認結果表示カード（管理アプリ用）
 *
 * タスク詳細のメッセージ表示内で、承認決定 1 件（承認方法・決定・理由・日時）を表示する。
 * インシデント調査時の主たる参照先（デスクトップの ApprovalResultDisplay と文言を統一）。
 */

import { AlertCircle, ShieldCheck, Sparkles } from 'lucide-react';
import { getApprovalResultLabel } from './approval-labels';
import type { ApprovalResultPart } from './task-detail-types';

export function ApprovalResultCard({ part }: { part: ApprovalResultPart }) {
  const isApproved = part.decision?.startsWith('allow') === true;

  return (
    <div
      data-decision={isApproved ? 'approved' : 'rejected'}
      className={`rounded-md border p-3 text-sm bg-card ${
        isApproved ? 'border-green-500/30' : 'border-red-500/30'
      }`}
    >
      <div className="flex items-start gap-2">
        <div
          className={`mt-0.5 ${isApproved ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
        >
          {isApproved ? <ShieldCheck className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          {/* ツール名と承認方法 */}
          <div className="flex items-center gap-2 flex-wrap">
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono font-semibold text-foreground">
              {part.toolName}
            </code>
            <span
              className={`text-xs font-medium ${isApproved ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
            >
              {getApprovalResultLabel(part.decision, part.source)}
            </span>
            {part.decidedAt && (
              <span
                data-testid="approval-result-decided-at"
                className="ml-auto text-xs text-muted-foreground"
              >
                {new Date(part.decidedAt).toLocaleString('ja-JP')}
              </span>
            )}
          </div>

          {/* 操作説明 */}
          {part.description && (
            <p className="mt-1 text-xs text-muted-foreground">{part.description}</p>
          )}

          {/* AI判定理由 */}
          {part.aiJudgmentReason && (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
              <Sparkles className="w-3 h-3 mt-0.5 shrink-0" />
              <span>AI判定理由: {part.aiJudgmentReason}</span>
            </div>
          )}

          {/* 拒否理由・自動決定の説明 */}
          {part.reason && (
            <div
              className={`mt-2 text-xs ${isApproved ? 'text-muted-foreground' : 'text-red-600 dark:text-red-400'}`}
            >
              理由: {part.reason}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
